/**
 * PickerFilters — the CREATE-BOOKING picker's fixed filter row (BON-142): month
 * nav (calendar view only), region tree-select, Calendar/List toggle, and the
 * tree-type chips.
 *
 * The rebook picker has its own filter row (`RebookPickerFilters`) rather than a
 * mode flag on this one: its region is a fixed label, not a control, and a
 * component that renders "either a dropdown or a label depending" is the kind of
 * seam that quietly grows a third case.
 */
import type { RegionNode } from '../../../api/regions';

import { FilterChipRow } from './FilterChipRow';
import { PickerRegionFilter } from './PickerRegionFilter';
import type { PickerViewMode } from './pickerViewMode';
import { ViewModeToggle } from './ViewModeToggle';
import type { FilterOption } from './WorkshopPickerHelpers';
import { WorkshopPickerMonthNav } from './WorkshopPickerMonthNav';

export interface PickerFiltersProps {
  regions: RegionNode[];
  regionId: string | null;
  onRegionChange: (id: string | null) => void;
  /** The viewer may create bookings only in their own regions. */
  confined?: boolean;
  treeOptions: FilterOption[];
  treeSpeciesId: string | null;
  onTreeChange: (id: string | null) => void;
  viewMode: PickerViewMode;
  onViewModeChange: (mode: PickerViewMode) => void;
  /** Calendar month nav — only meaningful (and rendered) in calendar view; the
   *  list view groups by month inline instead. */
  month: Date;
  onMonthChange: (month: Date) => void;
}

export function PickerFilters({
  regions,
  regionId,
  onRegionChange,
  confined = false,
  treeOptions,
  treeSpeciesId,
  onTreeChange,
  viewMode,
  onViewModeChange,
  month,
  onMonthChange,
}: PickerFiltersProps) {
  return (
    <div className="space-y-3 border-b border-stone-200 px-5 py-3 dark:border-stone-700">
      {viewMode === 'calendar' && (
        <WorkshopPickerMonthNav month={month} onMonthChange={onMonthChange} />
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PickerRegionFilter
          regions={regions}
          regionId={regionId}
          onRegionChange={onRegionChange}
          confined={confined}
        />
        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
      </div>
      <FilterChipRow
        ariaLabel="Filter by tree type"
        allLabel="All trees"
        options={treeOptions}
        activeId={treeSpeciesId}
        onChange={onTreeChange}
      />
    </div>
  );
}
